import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import PermissionGate from "../common/PermissionGate";
import ImageLightbox from "../common/ImageLightbox";
import { ForceStatusButton } from "./ForceStatusPopover";
import { PERMISSIONS } from "../../constants/permissions";
import "./Projects.css";
import useAuth from "../../hooks/useAuth";
import { buildOrderedFieldKeys } from "../../utils/cardFieldOrder";
import { getCurrentStageNames } from "../../utils/currentStage";
import { formatProjectStatus } from "../../utils/formatProjectStatus";

export default function ProjectCard({
  project,
  projectCardFields = [],
  cardFieldOrder,
  workAssignments = [],
  onEdit,
  onHold,
  onResume,
  onArchive,
  onDelete,
  onFlagForInvoicing,
  onStatusForced,
  showSelection = false,
  isSelected = false,
  onSelectionChange,
  showFolder = false,
  showFolderMembership = false,
  mySubmittedAwaitingReview = false,
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const canViewInvoicing =
    !!user?.isAdmin ||
    !!user?.permissions?.includes(PERMISSIONS.INVOICING_VIEW);
  const canManageInvoicing =
    !!user?.isAdmin ||
    !!user?.permissions?.includes(PERMISSIONS.INVOICING_MANAGE) ||
    (project.allocatedUsers || []).some((u) => u.id === user?.id);

  const getAlert = () => {
    if (!project?.createdAt || !project?.durationValue) return null;
    const durationMs =
      project.durationUnit === "HOURS"
        ? project.durationValue * 60 * 60 * 1000
        : project.durationValue * 24 * 60 * 60 * 1000;
    const elapsed = Date.now() - new Date(project.createdAt).getTime();
    const percent =
      durationMs > 0 ? Math.round((elapsed / durationMs) * 100) : 0;
    if (percent >= 100) return { level: "overdue", percent };
    if (percent >= 80) return { level: "warning", percent };
    return null;
  };
  const alert = getAlert();
  const projectImages = (
    Array.isArray(project?.imageUrl)
      ? project.imageUrl
      : project?.imageUrl
        ? [project.imageUrl]
        : []
  ).filter((url) => !url.toLowerCase().endsWith(".stl"));
  const primaryImage = projectImages[0] || "";
  const customFieldValues =
    project?.customFields && typeof project.customFields === "object"
      ? project.customFields
      : {};
  const folderNames = Array.isArray(project?.folderNames)
    ? project.folderNames
    : Array.isArray(project?.folders)
      ? project.folders.map((folder) => folder?.name).filter(Boolean)
      : [];
  const hasPendingReview = !!project?.hasPendingReview;
  const pendingReviewStages = Array.isArray(project?.pendingReviewStages)
    ? project.pendingReviewStages
    : [];
  const pendingReviewTooltip = pendingReviewStages.length
    ? `Waiting review for: ${pendingReviewStages.join(", ")}`
    : "Waiting on your review";

  const isAllocated = project?.allocatedUsers?.some((u) => u.id === user?.id);
  const awaitingInvoicingReview =
    project?.status === "AWAITING_INVOICE" &&
    isAllocated &&
    !user?.isAdmin;

  const customCardFieldKeys = projectCardFields.map((f) => f.key);
  const orderedKeys = buildOrderedFieldKeys(cardFieldOrder, customCardFieldKeys);

  const renderChip = (key) => {
    switch (key) {
      case "customer":
        return (
          <span key="customer" className="folder-membership-chip">
            Customer: {project.customer?.name || "-"}
          </span>
        );
      case "workflow":
        return (
          <span key="workflow" className="project-meta-chip">
            Workflow: {project.workflow?.name || "-"}
          </span>
        );
      case "folder":
        return showFolder ? (
          <span key="folder" className="project-meta-chip">
            Folder: {project.folder?.name || "Unfiled"}
          </span>
        ) : null;
      case "duration":
        return !["COMPLETED", "AWAITING_INVOICE"].includes(project.status) ? (
          <span key="duration" className="project-meta-chip">
            Duration: {project.durationValue} {project.durationUnit}
          </span>
        ) : null;
      case "stage":
        return !["COMPLETED", "AWAITING_INVOICE"].includes(project.status) ? (
          <span key="stage" className="project-meta-chip">
            Stage: {getCurrentStageNames(project).join(", ") || "Not started"}
          </span>
        ) : null;
      case "allocated":
        return (
          <span key="allocated" className="project-meta-chip">
            Allocated:{" "}
            {project.allocatedUsers?.length
              ? project.allocatedUsers
                  .map((u) => `${u.firstName} ${u.lastName}`)
                  .join(", ")
              : "None"}
          </span>
        );
      case "stageAssignee":
        if (project.currentStageAssignments?.length) {
          return (
            <span key="stageAssignee" className="project-meta-chip">
              Stage assignees:{" "}
              {project.currentStageAssignments
                .map((item) =>
                  `${item.assignee?.firstName || ""} ${item.assignee?.lastName || ""}`.trim(),
                )
                .filter(Boolean)
                .join(", ")}
            </span>
          );
        }
        if (project.currentStageAssignee) {
          return (
            <span key="stageAssignee" className="project-meta-chip">
              Stage assignee: {project.currentStageAssignee.firstName}{" "}
              {project.currentStageAssignee.lastName}
            </span>
          );
        }
        return null;
      default: {
        const field = projectCardFields.find((f) => f.key === key);
        if (!field) return null;
        return (
          <span
            key={field.key}
            className={field.highlightOnCard ? "project-meta-chip--highlight" : "project-meta-chip"}
          >
            {field.label}: {customFieldValues[field.key] || "-"}
          </span>
        );
      }
    }
  };

  return (
    <article className="project-card flex justify-center">
      {primaryImage ? (
        <button
          type="button"
          className="project-card-image"
          onClick={() => setLightboxIndex(0)}
          aria-label={`View ${project.name} images`}
          title="View images"
        >
          <img
            src={`${process.env.REACT_APP_API_UPLOAD_URL}${primaryImage}`}
            alt={project.name}
          />
          {projectImages.length > 1 && (
            <span className="project-card-image-count">
              {projectImages.length}
            </span>
          )}
        </button>
      ) : (
        <div className="project-img-placeholder">No Image</div>
      )}

      <div className="project-content">
        <div className="project-title-row">
          {showSelection && (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={(event) =>
                onSelectionChange?.(project.id, event.target.checked)
              }
              aria-label={`Select ${project.name}`}
            />
          )}
          <h3 style={{ margin: 0 }}>{project.name}</h3>
        </div>
        <div className="project-meta-chips">
          {orderedKeys.map((key) => renderChip(key))}
        </div>
        {showFolderMembership && folderNames.length > 0 && (
          <div className="project-folder-chips">
            {folderNames.map((name) => (
              <span key={name} className="folder-membership-chip">
                {name}
              </span>
            ))}
          </div>
        )}
        {alert &&
          !["COMPLETED", "AWAITING_INVOICE"].includes(project.status) && (
            <div className="project-alert">
              <span
                className={`project-alert-badge ${
                  alert.level === "overdue" ? "overdue" : "warning"
                }`}
              >
                {alert.level === "overdue" ? "Overdue" : "80% Warning"}
              </span>
              <span className="project-alert-meta">
                {alert.percent}% elapsed
              </span>
            </div>
          )}
      </div>

      <div className="project-right">
        <div className="project-status-row">
          <div className={`status-badge status-${project.status}`}>
            {formatProjectStatus(project.status)}
          </div>
          {/* Admin-only by design — never granted through a role's permissions. */}
          {user?.isAdmin && onStatusForced && (
            <ForceStatusButton project={project} onForced={onStatusForced} />
          )}
        </div>
        {canViewInvoicing &&
          project.invoicingStatus === "READY_FOR_INVOICING" && (
            <span className="invoicing-badge">Ready to Invoice</span>
          )}
        {hasPendingReview && (
          <span className="review-pending-badge" title={pendingReviewTooltip}>
            Waiting Review
          </span>
        )}
        {mySubmittedAwaitingReview && (
          <span
            className="submitted-awaiting-review-badge"
            title="You submitted this stage and it is awaiting review."
          >
            Submitted
          </span>
        )}
        {awaitingInvoicingReview && (
          <span
            className="review-pending-badge"
            title="This project is completed and awaiting invoicing review."
          >
            Awaiting Invoice
          </span>
        )}
        {canViewInvoicing && project.invoicingStatus === "INVOICED" && (
          <span className="invoiced-badge">Invoiced</span>
        )}
        {project.isUrgent && <span className="urgent-badge">Urgent</span>}
        <div className="project-actions">
          <Link className="btn-secondary" to={`/projects/${project.id}`}>
            View
          </Link>
          {!["ON_HOLD", "AWAITING_INVOICE"].includes(project.status) &&
            workAssignments.map((workAssignment) => (
              <Link
                key={workAssignment.id}
                className="btn-primary"
                to={`/projects/${project.id}/stage/${workAssignment.id}`}
              >
                {`Do Tasks — ${workAssignment.stageName || "Stage"}`}
              </Link>
            ))}
          <PermissionGate permission={PERMISSIONS.PROJECT_ASSIGN_USERS}>
            {!["COMPLETED", "AWAITING_INVOICE"].includes(project.status) &&
              (user.isAdmin ||
                project.allocatedUsers?.some((u) => u.id === user.id)) && (
                <button
                  className="btn-primary"
                  onClick={() => navigate(`/projects/${project.id}/manage`)}
                >
                  Manage
                </button>
              )}
          </PermissionGate>
          <PermissionGate
            permission={[
              PERMISSIONS.PROJECT_CREATE,
              PERMISSIONS.PROJECT_ASSIGN_USERS,
            ]}
          >
            {!["COMPLETED", "AWAITING_INVOICE"].includes(project.status) && !!onEdit && (
              <button className="btn-secondary" onClick={onEdit}>
                Edit
              </button>
            )}
          </PermissionGate>
          {(user?.isAdmin ||
            project.allocatedUsers?.some((u) => u.id === user?.id)) && (
            <>
              {!["COMPLETED", "AWAITING_INVOICE", "ON_HOLD"].includes(project.status) && (
                  <button className="btn-danger" onClick={onHold}>
                    Put On Hold
                  </button>
                )}
              {project.status === "ON_HOLD" && (
                <button className="btn-primary" onClick={onResume}>
                  Resume
                </button>
              )}
            </>
          )}
          <PermissionGate permission={PERMISSIONS.PROJECT_DELETE}>
            <button className="btn-secondary" onClick={onArchive}>
              Archive
            </button>
            {/* {project.status !== "IN_PROGRESS" && (
              <button className="btn-danger" onClick={onDelete}>
                Delete
              </button>
            )} */}
          </PermissionGate>
          {(canManageInvoicing || awaitingInvoicingReview) &&
            project.status === "COMPLETED" &&
            project.invoicingStatus === "NOT_READY" &&
            onFlagForInvoicing && (
              <button className="btn-secondary" onClick={onFlagForInvoicing}>
                Flag for Invoicing
              </button>
            )}
        </div>
      </div>
      {lightboxIndex !== null && (
        <ImageLightbox
          images={projectImages}
          index={lightboxIndex}
          onNavigate={setLightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </article>
  );
}
